import { supabase } from "../lib/supabase";
import { getCurrentPlatform } from "../utils/platform";
import {
  buildAccreditationTypes,
  buildCheckoutSourceMetadata,
  buildPaymentDiscountPayload,
  buildPaymentMetadata,
  buildRefresherInfo,
  getPaymentPartnerId,
} from "../utils/paymentPayloads";
import { authService } from "./authService";

export const COURSE_UNAVAILABLE_FROM_STORE = "COURSE_UNAVAILABLE_FROM_STORE";

// Purchase views read payments through these explicit column lists (report
// 10, FLOW-13/14). Never the raw payment_gateway_response (it holds gateway
// signatures and responses): only single server-written keys, projected by
// path. course_ids is the server-resolved list (bundles expanded); course_id
// is the server-priced single course of an order.
const SERVER_COURSE_KEYS = [
  "cart_course_ids:payment_gateway_response->>course_ids",
  "single_course_id:payment_gateway_response->>course_id",
  "batch_id:payment_gateway_response->>batch_id",
];

export const PURCHASE_HISTORY_COLUMNS = [
  "id",
  "course_id",
  "amount",
  "currency",
  "payment_status",
  "fulfillment_status",
  "created_at",
  "paid_at",
  ...SERVER_COURSE_KEYS,
].join(", ");
export const PURCHASE_HISTORY_LIMIT = 100;

export const HELD_PAYMENT_COLUMNS = [
  "id",
  "user_id",
  "course_id",
  "amount",
  "currency",
  "payment_status",
  "fulfillment_status",
  "fulfillment_error",
  "fulfilled_at",
  "refund_status",
  "platform",
  "created_at",
  "paid_at",
  "razorpay_order_id",
  "razorpay_payment_id",
  "stripe_session_id",
  ...SERVER_COURSE_KEYS,
  "held_at:payment_gateway_response->fulfillment_hold->>held_at",
  "noted_course_id:payment_gateway_response->fulfillment_hold->>noted_course_id",
  "user:user_profiles(full_name, email)",
  "course:courses(title)",
].join(", ");
export const HELD_PAYMENT_LIMIT = 200;

const parseIdList = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch {
    return [];
  }
};

// The courses a payment row covers, from server-written fields only.
const getRowCourseIds = (row) => [
  ...new Set(
    [
      row?.course_id,
      row?.single_course_id,
      ...parseIdList(row?.cart_course_ids),
    ]
      .filter(Boolean)
      .map(String),
  ),
];

async function getCourseTitles(courseIds) {
  if (courseIds.length === 0) return new Map();
  const { data } = await supabase
    .from("courses")
    .select("id, title")
    .in("id", courseIds);
  return new Map(
    (data || []).map((course) => [String(course.id), course.title]),
  );
}

async function readFunctionErrorBody(error) {
  const response = error?.context;
  if (!(response instanceof Response)) return null;

  return response
    .clone()
    .json()
    .catch(() => null);
}

async function normalizeStripeSessionError(error) {
  const body = await readFunctionErrorBody(error);
  const message = body?.error || error?.message || "Failed to create Stripe session";

  if (/not currently available from this store/i.test(message)) {
    const unavailable = new Error(
      "This course is no longer available from this store. Please refresh and choose an available course.",
    );
    unavailable.code = COURSE_UNAVAILABLE_FROM_STORE;
    unavailable.responsePayload = body;
    return unavailable;
  }

  return new Error(message);
}

class PaymentService {
  async requireFreshSession(expectedUserId) {
    await authService.ensureFreshToken();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error("Please sign in to continue");
    }

    if (expectedUserId && session.user.id !== expectedUserId) {
      throw new Error("User authentication mismatch");
    }

    return session;
  }

  async createPayment(paymentData) {
    try {
      const { data, error } = await supabase
        ?.from("payments")
        ?.insert([
          {
            ...paymentData,
            platform: paymentData.platform || getCurrentPlatform(),
          },
        ])
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(error?.message || "Failed to create payment record");
    }
  }

  async updatePaymentStatus(paymentId, status, gatewayData = {}) {
    void paymentId;
    void status;
    void gatewayData;
    throw new Error(
      "Payment status is provider-owned and cannot be updated from the browser.",
    );
  }

  async updatePaymentWithOrderId(
    paymentId,
    razorpayOrderId,
    paymentUpdates = {},
  ) {
    void paymentId;
    void razorpayOrderId;
    void paymentUpdates;
    throw new Error(
      "Provider order identity is server-owned and cannot be updated from the browser.",
    );
  }

  async updatePaymentWithRazorpay(razorpayOrderId, razorpayPaymentId, status) {
    void razorpayOrderId;
    void razorpayPaymentId;
    void status;
    throw new Error(
      "Razorpay fulfillment is server-owned and cannot be updated from the browser.",
    );
  }

  async getUserPayments(userId) {
    try {
      const { data, error } = await supabase
        ?.from("payments")
        ?.select(
          `
          *,
          course:courses (
            title,
            category
          ),
          enrollment:course_enrollments (
            enrollment_status
          )
        `,
        )
        ?.eq("user_id", userId)
        .eq("platform", getCurrentPlatform())
        ?.order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error(error?.message || "Failed to fetch payment history");
    }
  }

  // Learner "My purchases": the caller's own rows (users_view_own_payments
  // also enforces it), newest first, with course titles and whether the
  // learner can currently open each course. A failed access read is
  // returned as accessByCourseId = null ("unknown"), never as "no access".
  async getPurchaseHistory(userId) {
    const { data, error } = await supabase
      .from("payments")
      .select(PURCHASE_HISTORY_COLUMNS)
      .eq("user_id", userId)
      .eq("platform", getCurrentPlatform())
      .order("created_at", { ascending: false })
      .limit(PURCHASE_HISTORY_LIMIT);
    if (error) throw new Error(error.message || "Failed to load purchases");

    const purchases = (data || []).map((row) => ({
      ...row,
      courseIds: getRowCourseIds(row),
    }));
    const courseIds = [...new Set(purchases.flatMap((row) => row.courseIds))];
    if (courseIds.length === 0) {
      return {
        purchases,
        courseTitles: new Map(),
        accessByCourseId: new Set(),
      };
    }
    const [courseTitles, access] = await Promise.all([
      getCourseTitles(courseIds),
      supabase
        .from("course_enrollments")
        .select("course_id")
        .eq("user_id", userId)
        .in("course_id", courseIds)
        .in("enrollment_status", ["active", "completed"]),
    ]);
    return {
      purchases,
      courseTitles,
      accessByCourseId: access.error
        ? null
        : new Set((access.data || []).map((row) => String(row.course_id))),
    };
  }

  // Platform-admin queue of payments held for manual review (fulfillment
  // _status = 'manual_review'; "Admins can view all payments" = is_admin()).
  // Read-only: there is deliberately no grant or refund call here.
  async getHeldPayments() {
    const { data, error, count } = await supabase
      .from("payments")
      .select(HELD_PAYMENT_COLUMNS, { count: "exact" })
      .eq("fulfillment_status", "manual_review")
      .order("created_at", { ascending: false })
      .limit(HELD_PAYMENT_LIMIT);
    if (error) throw new Error(error.message || "Failed to load held payments");

    const rows = (data || []).map((row) => ({
      ...row,
      courseIds: getRowCourseIds(row),
    }));
    const courseIds = [
      ...new Set(
        rows.flatMap((row) =>
          [...row.courseIds, row.noted_course_id].filter(Boolean).map(String),
        ),
      ),
    ];
    return {
      rows,
      total: count ?? rows.length,
      courseTitles: await getCourseTitles(courseIds),
    };
  }

  // Create Razorpay Order via Edge Function
  async createPaymentOrder(
    purchaseId,
    amount,
    currency = "INR",
    receipt,
    notes = {},
  ) {
    try {
      await this.requireFreshSession();
      const partnerId = getPaymentPartnerId(notes);
      const accreditationTypes = buildAccreditationTypes(notes);
      const refresherInfo = buildRefresherInfo(notes);
      const discountPayload = buildPaymentDiscountPayload(notes);
      const checkoutSourceMetadata = buildCheckoutSourceMetadata({
        url: notes.sourceUrl || notes.source_url,
        partnerId,
      });
      const siteUrl =
        notes.site_url ||
        notes.siteUrl ||
        (typeof window !== "undefined" ? window.location.origin : undefined);

      const { data, error } = await supabase.functions.invoke(
        "create-payment-order",
        {
          body: {
            purchaseId, // Can be null for direct buys
            amount,
            currency,
            receipt,
            // Extract teamId and courseId from notes if present, as backend expects them at top level
            teamId: partnerId,
            courseId: notes.course_id || notes.courseId,
            ...discountPayload,
            accreditationTypes,
            refresherInfo,
            siteUrl,
            checkoutSourceMetadata,
            notes,
          },
        },
      );

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Create payment order error:", error);
      throw new Error(error.message || "Failed to create payment order");
    }
  }

  // Verify Payment via Edge Function
  async verifyPayment(paymentData) {
    try {
      await this.requireFreshSession();

      const { data, error } = await supabase.functions.invoke(
        "verify-payment",
        {
          body: paymentData,
        },
      );

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Verify payment error:", error);
      throw new Error(error.message || "Failed to verify payment");
    }
  }

  // Create Stripe Checkout Session via Edge Function (for USD/CAD)
  async createStripeSession(paymentData) {
    try {
      await this.requireFreshSession(paymentData.user_id);
      const partnerId = getPaymentPartnerId(paymentData);
      const metadata = buildPaymentMetadata({
        ...paymentData,
        partnerId,
      });

      const { data, error } = await supabase.functions.invoke(
        "stripe-payment",
        {
          body: {
            paymentData: {
              amount: paymentData.amount,
              currency: paymentData.currency || "usd",
              course_id: paymentData.course_id,
              description: paymentData.description,
              customer_email: paymentData.customer_email,
              partner_id: partnerId,
              discountCodeId: paymentData.discountCodeId || null,
              discountCode: paymentData.discountCode || null,
              discountAmount: paymentData.discountAmount || 0,
              quantityDiscountAmount: paymentData.quantityDiscountAmount || 0,
              originalAmount: paymentData.originalAmount || null,
              metadata,
            },
          },
        },
      );

      if (error) throw error;
      return data;
    } catch (error) {
      const normalizedError = await normalizeStripeSessionError(error);
      if (normalizedError.code !== COURSE_UNAVAILABLE_FROM_STORE) {
        console.error("Create Stripe session error:", error);
      }
      throw normalizedError;
    }
  }
}

export const paymentService = new PaymentService();
